import type { AuthoredDesign, DefinitionProblem, DefinitionReadiness, GoLiveOutcome, OrganisationAllowance } from "@keydials/api-client";
import { builderCopy as copy } from "./copy";
import type { LimitSheetView } from "./limitSheetView";

// ---------------------------------------------------------------------------
// Going live (KEY-560): the author's dial published if it needs to be, and its running dial made with them as its administrator,
// in one server call. Everything unsaved is saved first and editing stops while the call is out, so no queued save meets a
// published dial. What the server answers decides where the author goes next; the calls are passed in so every answer can be
// tested without a screen.
// ---------------------------------------------------------------------------

export interface LaunchDeps {
  /** Saves anything unsaved. Resolves true when nothing typed is left unsaved. */
  flush(): Promise<boolean>;
  /** Stops edits and saves while the dial goes live. */
  freeze(): void;
  /** Lets editing carry on after going live was refused. */
  thaw(): void;
  goLive(): Promise<GoLiveOutcome>;
}

export type LaunchResult =
  | { kind: "live"; instanceId: string }
  | { kind: "notReady"; problems: DefinitionProblem[] }
  | { kind: "running"; instanceId: string }
  | { kind: "limit"; message: string }
  | { kind: "refused"; message: string }
  | { kind: "notSaved" }
  | { kind: "trialEnded" }
  | { kind: "gone" }
  | { kind: "failed" };

const statusOf = (error: unknown): number | null => {
  const status = (error as { status?: unknown } | null)?.status;
  return typeof status === "number" ? status : null;
};

/**
 * Takes the dial live.
 * - Live: the running dial, where the author invites people next. Editing stays stopped: the design is published.
 * - Not ready (400 with problems): the checklist. The plan or a seat refusing (400 without): the server's sentence, in the limit
 *   sheet. Either way editing carries on.
 * - Already live (409): the running dial it names. Archived (409 without one): the server's sentence.
 * - The trial ended (402), or the design is gone (404): the session is over. Anything else: say so, and carry on.
 */
export async function runGoLive(deps: LaunchDeps): Promise<LaunchResult> {
  if (!(await deps.flush())) return { kind: "notSaved" };
  deps.freeze();

  let outcome: GoLiveOutcome;
  try {
    outcome = await deps.goLive();
  } catch (error) {
    const status = statusOf(error);
    if (status === 402) return { kind: "trialEnded" };
    if (status === 404) return { kind: "gone" };
    deps.thaw();
    return { kind: "failed" };
  }

  if (outcome.ok) return { kind: "live", instanceId: outcome.instanceId };
  if (outcome.status === 409) {
    return outcome.instanceId ? { kind: "running", instanceId: outcome.instanceId } : { kind: "refused", message: outcome.message };
  }
  deps.thaw();
  return outcome.problems.length > 0 ? { kind: "notReady", problems: outcome.problems } : { kind: "limit", message: outcome.message };
}

/** The limit sheet for a refusal going live met: the plan's name, when known, and the server's sentence word for word. */
export function goLiveSheet(message: string, allowance: OrganisationAllowance | null): LimitSheetView {
  return { title: allowance?.plan ? `${allowance.plan.family} ${allowance.plan.tier}` : copy.limits.yourPlan, message };
}

/** A readiness answer carrying the problems going live reported, which are newer than the last one read. */
export function withProblems(readiness: DefinitionReadiness, problems: DefinitionProblem[]): DefinitionReadiness {
  return { ...readiness, ready: problems.length === 0, problems };
}

/** The running dial of one of the author's designs, when it has one. */
export function runningDialOf(designs: readonly AuthoredDesign[], id: string): string | null {
  return designs.find((design) => design.id === id)?.liveInstanceId ?? null;
}
